import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Mail, AlertTriangle, Info, RefreshCw } from 'lucide-react'
import { client } from '@/api/client'
import { AuthLayout } from '@/components/AuthLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuthStore } from '@/stores/auth'

export function VerificationPendingPage() {
  const { t } = useTranslation('auth')
  const parent = useAuthStore((state) => state.parent)
  const [isResending, setIsResending] = useState(false)
  const [resendMessage, setResendMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (parent && parent.is_email_verified) {
      navigate('/')
    }
  }, [parent, navigate])

  const handleResendVerification = async () => {
    try {
      setIsResending(true)
      setResendMessage(null)
      await client.post('/auth/resend-verification/')
      setResendMessage({
        type: 'success',
        text: t('verification.pending.resendSuccess')
      })
    } catch (error: any) {
      if (error.response?.status === 429) {
        if (error.response.data?.rate_limited && error.response.data?.minutes_remaining) {
          setResendMessage({
            type: 'error',
            text: t('verification.pending.rateLimitedWithTime', {
              minutes: error.response.data.minutes_remaining
            })
          })
        } else {
          setResendMessage({
            type: 'error',
            text: t('verification.pending.rateLimited')
          })
        }
      } else {
        setResendMessage({
          type: 'error',
          text: error.response?.data?.message || t('verification.pending.resendError')
        })
      }
    } finally {
      setIsResending(false)
    }
  }

  return (
    <AuthLayout showLogout>
      <div className="flex justify-center mb-8">
        <img src="/images/logo.png" alt="The Ranch" className="h-20 w-auto" />
      </div>

      <Card>
        <CardHeader className="text-center">
          <div className="rounded-full bg-amber-100 p-3 w-fit mx-auto mb-2">
            <Mail className="h-8 w-8 text-amber-600" />
          </div>
          <CardTitle className="text-xl">{t('verification.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Main message */}
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-amber-800">
                {t('verification.pending.message', {
                  email: parent?.email,
                  interpolation: { escapeValue: false }
                })}
              </p>
            </div>
          </div>

          {/* Spam folder hint */}
          <div className="rounded-lg bg-sky-50 border border-sky-200 p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-sky-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-sky-800">{t('verification.pending.spamMessage')}</p>
            </div>
          </div>

          {/* Resend section */}
          <div className="text-center space-y-3 pt-2">
            <p className="text-sm font-medium">{t('verification.pending.resend')}</p>

            {resendMessage && (
              <div className={`rounded-md p-3 text-sm ${
                resendMessage.type === 'success'
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                  : 'bg-destructive/10 border border-destructive/30 text-destructive'
              }`}>
                {resendMessage.text}
              </div>
            )}

            <Button
              onClick={handleResendVerification}
              disabled={isResending}
              variant="outline"
              className="w-full"
            >
              {isResending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t('verification.pending.resending')}
                </>
              ) : (
                t('verification.pending.resendButton')
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
